import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    Box, Stack, Typography, Button, TextField, MenuItem, Avatar, Chip, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, CircularProgress,
    Drawer, IconButton, Divider,
} from '@mui/material';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { useSelector } from 'react-redux';
import { selectUser, selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';
import http, { apiErrorMessage } from '../Api/http';
import {
    GetResignations, SubmitResignation, SetResignationStatus, ApproveResignation, CancelNoticePeriod, UpdateResignation, DeleteResignation,
    GetMyResignation, WithdrawResignation, GetFinalSettlement, GetEmployeeFinalSettlement, GetResignationSetting, SaveResignationSetting,
} from '../Api/Api';
import { PRIMARY } from '../theme';
import { card, solidBtn, ghostBtn, tonalBtn, field, PageHeader,EmptyState, ConfirmDialog } from '../components/uiKit';
import { initialsFromName as initials, avatarTone, fmtDate, isoFromDMY, inr } from '../utils/format';
import { Segmented } from '../components/LeaveAttendanceComps/punchUi';
import { passphraseConfig, isPassphraseError, passphraseErrorText } from '../utils/salaryPassphrase';

const REASON_CATEGORIES = [
    'Better Opportunity', 'Personal Reasons', 'Relocation', 'Higher Studies', 'Health',
    'Work Environment', 'Compensation', 'Retirement', 'Termination', 'Other',
];

const STATUS_STYLE = {
    Pending: { label: 'Pending', color: '#B45309', bg: '#FFF7ED' },
    Approved: { label: 'Serving notice', color: '#0369A1', bg: '#E0F2FE' },
    Relieved: { label: 'Relieved', color: '#16A34A', bg: '#DCFCE7' },
    Withdrawn: { label: 'Withdrawn', color: '#64748B', bg: '#F1F5F9' },
    Cancelled: { label: 'Notice cancelled', color: '#9A3412', bg: '#FFEDD5' },
};
const StatusPill = ({ status }) => {
    const s = STATUS_STYLE[status] || { label: status || '—', color: '#64748B', bg: '#F1F5F9' };
    return <Chip label={s.label} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: s.bg, color: s.color }} />;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const toApiDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return d ? `${d}-${m}-${y}` : iso;
};
// The old version sliced to 10 characters, which chopped the year off an
// already-formatted date ("11 Aug 2026" -> "11 Aug 202"). Parse what we get and
// render one consistent format instead.
const showDate = (v) => {
    if (!v) return '—';
    const s = String(v).trim();
    const t = new Date(isoFromDMY(s));
    return Number.isNaN(t.getTime()) ? s : fmtDate(t);
};
const toInputDate = (v) => {
    if (!v) return '';
    const t = new Date(isoFromDMY(String(v).trim()));
    if (Number.isNaN(t.getTime())) return '';
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};
const daysBetween = (fromISO, toISO) => {
    if (!fromISO || !toISO) return 0;
    const a = new Date(fromISO), b = new Date(toISO);
    return Math.max(0, Math.round((b - a) / 86400000));
};

// Shared field-mapping for a resignation record, however the caller's screen
// (management list vs an employee's own) happens to key its response.
const mapResignationRow = (r) => ({
    id: r.id ?? r.Id,
    resignationId: r.id ?? r.Id ?? r.resignationId,
    employeeCode: r.employeeCode ?? r.EmployeeCode ?? '',
    employeeName: r.employeeName ?? r.EmployeeName ?? r.name ?? '',
    department: r.department ?? r.Department ?? '',
    designation: r.designation ?? r.Designation ?? '',
    resignationDate: r.resignationDate ?? r.ResignationDate ?? '',
    lastWorkingDay: r.lastWorkingDay ?? r.LastWorkingDay ?? '',
    noticeDays: r.noticeDays ?? r.NoticeDays ?? r.noticePeriodDays ?? null,
    noticePeriodStartDate: r.noticePeriodStartDate ?? null,
    daysRemaining: r.daysRemaining ?? null,
    reasonCategory: r.reasonCategory ?? r.ReasonCategory ?? '',
    reason: r.reason ?? r.Reason ?? '',
    status: r.status ?? r.Status ?? 'Pending',
    remarks: r.remarks ?? r.Remarks ?? '',
    approvedBy: r.approvedBy ?? null,
    approvedOn: r.approvedOn ?? null,
    relievedBy: r.relievedBy ?? null,
    relievedOn: r.relievedOn ?? null,
    cancelledBy: r.cancelledBy ?? null,
    cancelledOn: r.cancelledOn ?? null,
});

export default function ResignationsPage() {
    const user = useSelector(selectUser);
    const userTypeId = useSelector(selectUserTypeId);
    const isApprover = userTypeId === USER_TYPE.MASTER_ADMIN || userTypeId === USER_TYPE.ADMIN;
    const isMasterAdmin = userTypeId === USER_TYPE.MASTER_ADMIN;

    const [snack, setSnack] = useState(null);
    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    return (
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 3 }}>
            {isApprover
                ? <ApproverView notify={notify} isMasterAdmin={isMasterAdmin} userTypeId={userTypeId} user={user} />
                : <SelfRequestView user={user} notify={notify} />}

            <Snackbar
                open={!!snack}
                autoHideDuration={4000}
                onClose={() => setSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snack ? <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)} sx={{ borderRadius: '8px' }}>{snack.msg}</Alert> : undefined}
            </Snackbar>
        </Box>
    );
}

function ApproverView({ notify, isMasterAdmin, userTypeId, user }) {
    const [rows, setRows] = useState([]);
    const [, setServerCards] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [action, setAction] = useState(null); // { row, to, kind, remarks }
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [editRow, setEditRow] = useState(null); // { row, resignationDate, lastWorkingDay, reasonCategory, reason }
    const [busyId, setBusyId] = useState(null);
    const [resignOpen, setResignOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [settlementFor, setSettlementFor] = useState(null); // { resignationId, employeeName }
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Only an entity admin can action a resignation (approve/relieve/withdraw/
    // un-relieve/delete) — MasterAdmin reads across every entity but offboarding
    // itself is an entity-admin act.
    const canApprove = userTypeId === USER_TYPE.ADMIN;

    // Always available to an entity admin; the server enforces any rule such as
    // keeping at least one admin, and its message is shown if the submit is refused.
    const showAdminResign = userTypeId === USER_TYPE.ADMIN;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetResignations, {
                params: { status: statusFilter || undefined, search: search || undefined },
            });
            if (body?.error) throw new Error(body.message || 'Could not load resignations.');
            const d = body?.data ?? {};
            const list = Array.isArray(d) ? d : (d.resignations || d.items || d.list || []);
            setRows(list.map(mapResignationRow));
            setServerCards(d.cards || null);
            setLoadError('');
        } catch (err) {
            if (err?.response?.status === 404) { setRows([]); setServerCards(null); setLoadError(''); }
            else { setRows([]); setLoadError(apiErrorMessage(err, 'Could not load resignations.')); }
        } finally {
            setLoading(false);
        }
    }, [statusFilter, search]);

    useEffect(() => { load(); }, [load]);

    const runStatus = async () => {
        const { row, to, kind, remarks } = action;
        if (kind === 'approve' && (!action.startDate || !(Number(action.days) > 0))) {
            notify('Set the notice start date and a notice period of at least 1 day.', 'warning');
            return;
        }
        // Relieve stays open until it succeeds so a wrong Encryption Password can be retyped.
        if (kind !== 'relieve') setAction(null);
        setBusyId(row.id);
        try {
            let req;
            if (kind === 'approve') {
                req = http.put(ApproveResignation, {
                    id: row.id,
                    noticePeriodStartDate: toApiDate(action.startDate),
                    noticePeriodDays: Number(action.days),
                    notes: remarks || '',
                });
            } else if (kind === 'cancelnotice') {
                req = http.put(CancelNoticePeriod, { id: row.id, reason: remarks || '' });
            } else {
                const pass = kind === 'relieve' ? action.passphrase : '';
                req = http.put(SetResignationStatus, { id: row.id, status: to, remarks: remarks || '' }, pass ? passphraseConfig(pass) : undefined);
            }
            const { data: body } = await req;
            if (body?.error) throw new Error(body.message || 'Could not update the resignation.');
            setAction(null);
            const name = row.employeeName || row.employeeCode;
            if (kind === 'cancelnotice') notify(`${name}'s notice period was cancelled.`);
            else if (kind === 'relieve') notify(`${name} relieved — marked inactive.`);
            else if (kind === 'unrelieve') notify(`${name} un-relieved — settlement reversed, employee reactivated.`);
            else if (kind === 'withdraw') notify('Resignation withdrawn — the employee stays active.');
            else notify(body?.message || 'Resignation approved.');
            await load();
            setDetail(null);
            if (kind === 'relieve') setSettlementFor({ resignationId: row.id, employeeName: name });
        } catch (err) {
            if (kind === 'relieve' && isPassphraseError(err)) {
                setAction((a) => (a ? { ...a, error: passphraseErrorText(err) } : a));
            } else {
                setAction(null);
                notify(apiErrorMessage(err, isMasterAdmin
                    ? 'Could not update. As Master Admin, pick the entity in the header first.'
                    : 'Could not update the resignation.'), 'error');
            }
        } finally { setBusyId(null); }
    };

    // Management correction of a still-open resignation (UpdateResignation takes the whole record).
    const saveEdit = async () => {
        const e = editRow;
        if (!e.resignationDate || !e.lastWorkingDay) { notify('Both dates are required.', 'warning'); return; }
        if (e.lastWorkingDay < e.resignationDate) { notify('The last working day can’t be before the resignation date.', 'warning'); return; }
        setBusyId(e.row.id);
        try {
            const { data: body } = await http.put(UpdateResignation, {
                id: e.row.id,
                employeeCode: e.row.employeeCode,
                resignationDate: toApiDate(e.resignationDate),
                lastWorkingDay: toApiDate(e.lastWorkingDay),
                reasonCategory: e.reasonCategory,
                reason: e.reason,
            });
            if (body?.error) throw new Error(body.message || 'Could not update the resignation.');
            notify(body?.message || 'Resignation updated.');
            setEditRow(null);
            setDetail(null);
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not update the resignation.'), 'error');
        } finally { setBusyId(null); }
    };

    const removeRow = async (row) => {
        setConfirmDelete(null);
        setBusyId(row.id);
        try {
            const { data: body } = await http.delete(DeleteResignation, { params: { id: row.id } });
            if (body?.error) throw new Error(body.message || 'Could not delete the resignation.');
            notify('Resignation removed.');
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not delete. A relieved resignation must be withdrawn first.'), 'error');
        } finally { setBusyId(null); }
    };

    const FILTERS = ['', 'Pending', 'Approved', 'Relieved', 'Cancelled', 'Withdrawn'];
    const filterLabel = (f) => (f === '' ? 'All' : (STATUS_STYLE[f]?.label || f));

    // Search and the page actions live at the right end of the breadcrumb row.
    const { setCrumbActions } = useOutletContext() || {};
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                    size="small" placeholder="Search name / code / department"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    sx={{ ...field, width: { xs: 180, sm: 260 } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} /></InputAdornment> }}
                />
                {canApprove && (
                    <IconButton onClick={() => setSettingsOpen(true)} sx={{ border: '1px solid #E5E7EB', borderRadius: '7px' }} title="Resignation settings">
                        <SettingsRoundedIcon sx={{ fontSize: 19, color: '#475569' }} />
                    </IconButton>
                )}
                {showAdminResign && (
                    <Button
                        startIcon={<LogoutRoundedIcon sx={{ fontSize: 18 }} />}
                        onClick={() => setResignOpen(true)}
                        sx={{ ...ghostBtn, height: 40, px: 2 }}
                    >
                        Raise my resignation
                    </Button>
                )}
            </Box>,
        );
        return () => setCrumbActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, search, canApprove, showAdminResign]);

    return (
        <>
            <Box sx={{ mb: 1.4, display: 'flex' }}>
                <Segmented
                    items={FILTERS.map((f) => ({ key: f || 'all', label: filterLabel(f) }))}
                    value={statusFilter || 'all'}
                    onChange={(k) => setStatusFilter(k === 'all' ? '' : k)}
                />
            </Box>

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={26} /></Box>
            ) : loadError ? (
                <Box sx={{ ...card, p: 0 }}><EmptyState icon={LogoutRoundedIcon} title="Couldn't load resignations" hint={loadError} /></Box>
            ) : rows.length === 0 ? (
                <Box sx={{ ...card, p: 0 }}><EmptyState icon={LogoutRoundedIcon} title="No resignations" hint="Resignations your staff raise will appear here for approval." /></Box>
            ) : (
                <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: RES_MIN_W }}>
                            <Box sx={{
                                display: 'grid', gridTemplateColumns: RES_COLS, alignItems: 'center', gap: 2,
                                px: RES_PAD_X, py: 1.2, bgcolor: '#FAFAFD', borderBottom: '1px solid #E6EAF1',
                                borderLeft: '3px solid transparent',
                            }}>
                                <HeadCell>Employee</HeadCell>
                                <HeadCell>Resigned on</HeadCell>
                                <HeadCell>Last working day</HeadCell>
                                <HeadCell>Notice</HeadCell>
                                <span />
                            </Box>

                            {rows.map((r) => (
                                <ResignationRow
                                    key={r.id}
                                    r={r}
                                    busy={busyId === r.id}
                                    selected={detail?.id === r.id}
                                    onOpen={setDetail}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}

            <ResignationDetail
                r={detail}
                busy={busyId === detail?.id}
                readOnly={!canApprove}
                onClose={() => setDetail(null)}
                onApprove={() => setAction({ row: detail, to: 'Approved', kind: 'approve', remarks: '', startDate: todayISO(), days: String(noticeOf(detail) || 30) })}
                onCancelNotice={() => setAction({ row: detail, to: 'Cancelled', kind: 'cancelnotice', remarks: '' })}
                onEdit={() => setEditRow({ row: detail, resignationDate: toInputDate(detail.resignationDate), lastWorkingDay: toInputDate(detail.lastWorkingDay), reasonCategory: detail.reasonCategory || REASON_CATEGORIES[0], reason: detail.reason || '' })}
                onRelieve={() => setAction({ row: detail, to: 'Relieved', kind: 'relieve', remarks: '' })}
                onWithdraw={() => setAction({ row: detail, to: 'Withdrawn', kind: 'withdraw', remarks: '' })}
                onUnrelieve={() => setAction({ row: detail, to: 'Approved', kind: 'unrelieve', remarks: '' })}
                onDelete={() => setConfirmDelete(detail)}
                onViewSettlement={() => setSettlementFor({ resignationId: detail.id, employeeName: detail.employeeName || detail.employeeCode })}
            />

            <Dialog open={!!action} onClose={() => setAction(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                {action && (
                    <>
                        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>
                            {action.kind === 'approve' ? 'Approve resignation'
                                : action.kind === 'cancelnotice' ? 'Cancel notice period'
                                : action.kind === 'relieve' ? 'Relieve employee'
                                    : action.kind === 'unrelieve' ? 'Un-relieve employee'
                                        : 'Withdraw resignation'}
                        </DialogTitle>
                        <DialogContent>
                            <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 1.5 }}>
                                {action.kind === 'approve' && `${action.row.employeeName || action.row.employeeCode} moves to serving notice. Set when the notice period starts and how long it runs; they are notified.`}
                                {action.kind === 'cancelnotice' && `${action.row.employeeName || action.row.employeeCode}'s approved resignation is called off and they stay active. They are notified, and can resign again later.`}
                                {action.kind === 'relieve' && `${action.row.employeeName || action.row.employeeCode} will be marked INACTIVE and the Full & Final settlement will be generated automatically.`}
                                {action.kind === 'unrelieve' && `This reverses the Full & Final settlement, reopens any recovered advances, and reactivates ${action.row.employeeName || action.row.employeeCode}.`}
                                {action.kind === 'withdraw' && `The resignation is cancelled and ${action.row.employeeName || action.row.employeeCode} stays active.`}
                            </Typography>
                            {action.kind === 'approve' && (
                                <>
                                    <Stack direction="row" spacing={1.4} sx={{ mb: 1.6 }}>
                                        <TextField
                                            label="Notice starts" type="date" size="small" fullWidth required
                                            value={action.startDate} onChange={(e) => setAction((a) => ({ ...a, startDate: e.target.value }))}
                                            sx={field} slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                        <TextField
                                            label="Notice days" type="number" size="small" fullWidth required
                                            value={action.days} onChange={(e) => setAction((a) => ({ ...a, days: e.target.value }))}
                                            sx={field} slotProps={{ htmlInput: { min: 1 } }}
                                        />
                                    </Stack>
                                    {(() => {
                                        const d = new Date(`${action.startDate}T00:00:00`);
                                        const n = Number(action.days);
                                        if (!action.startDate || Number.isNaN(d.getTime()) || !(n > 0)) return null;
                                        d.setDate(d.getDate() + n);
                                        return (
                                            <Box sx={{ mb: 1.6, px: 1.4, py: 1, borderRadius: '8px', bgcolor: '#F1EEFE', border: '1px solid #C9BEFB' }}>
                                                <Typography sx={{ fontSize: 12, color: '#475569' }}>
                                                    Last working day: <b>{fmtDate(d)}</b>
                                                </Typography>
                                            </Box>
                                        );
                                    })()}
                                </>
                            )}
                            {action.kind === 'relieve' && (
                                <>
                                    {action.error && <Alert severity="error" sx={{ mb: 1.4, fontSize: 12, borderRadius: '7px' }}>{action.error}</Alert>}
                                    <TextField
                                        label="Encryption Password" type="password" size="small" fullWidth autoComplete="off"
                                        helperText="Needed when salary encryption is on, to compute the settlement from the real salary."
                                        value={action.passphrase || ''} onChange={(e) => setAction((a) => ({ ...a, passphrase: e.target.value, error: '' }))}
                                        sx={{ ...field, mb: 1.6 }} slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                </>
                            )}
                            <TextField
                                label={action.kind === 'approve' ? 'Notes (shared with the employee, optional)' : action.kind === 'cancelnotice' ? 'Reason (shown to the employee, optional)' : 'Remarks (optional)'}
                                size="small" fullWidth multiline minRows={2}
                                value={action.remarks} onChange={(e) => setAction((a) => ({ ...a, remarks: e.target.value }))}
                                sx={field}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                            <Button onClick={() => setAction(null)} sx={{ ...ghostBtn, height: 40 }}>Cancel</Button>
                            <Button onClick={runStatus} sx={{ ...solidBtn, height: 40, ...(action.kind === 'relieve' && { bgcolor: '#16A34A', '&:hover': { bgcolor: '#15803D' } }) }}>
                                {action.kind === 'approve' ? 'Approve' : action.kind === 'cancelnotice' ? 'Cancel notice' : action.kind === 'relieve' ? 'Relieve' : action.kind === 'unrelieve' ? 'Un-relieve' : 'Withdraw'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                {editRow && (
                    <>
                        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>Edit resignation</DialogTitle>
                        <DialogContent>
                            <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 1.6 }}>
                                Correct the record for {editRow.row.employeeName || editRow.row.employeeCode}. Use Approve to set the real notice terms.
                            </Typography>
                            <Stack direction="row" spacing={1.4} sx={{ mb: 1.6 }}>
                                <TextField
                                    label="Resignation date" type="date" size="small" fullWidth required
                                    value={editRow.resignationDate} onChange={(e) => setEditRow((r) => ({ ...r, resignationDate: e.target.value }))}
                                    sx={field} slotProps={{ inputLabel: { shrink: true } }}
                                />
                                <TextField
                                    label="Last working day" type="date" size="small" fullWidth required
                                    value={editRow.lastWorkingDay} onChange={(e) => setEditRow((r) => ({ ...r, lastWorkingDay: e.target.value }))}
                                    sx={field} slotProps={{ inputLabel: { shrink: true } }}
                                />
                            </Stack>
                            <TextField
                                select label="Reason category" size="small" fullWidth value={editRow.reasonCategory}
                                onChange={(e) => setEditRow((r) => ({ ...r, reasonCategory: e.target.value }))} sx={{ ...field, mb: 1.6 }}
                            >
                                {REASON_CATEGORIES.map((c) => <MenuItem key={c} value={c} sx={{ fontSize: 13.5 }}>{c}</MenuItem>)}
                            </TextField>
                            <TextField
                                label="Reason" size="small" fullWidth multiline minRows={2}
                                value={editRow.reason} onChange={(e) => setEditRow((r) => ({ ...r, reason: e.target.value }))} sx={field}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                            <Button onClick={() => setEditRow(null)} sx={{ ...ghostBtn, height: 40 }}>Cancel</Button>
                            <Button onClick={saveEdit} disabled={busyId === editRow.row.id} sx={{ ...solidBtn, height: 40 }}>Save changes</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <ConfirmDialog
                open={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={() => removeRow(confirmDelete)}
                title="Remove this resignation?"
                body="This soft-deletes the record. A relieved resignation must be withdrawn first."
                confirmLabel="Remove"
            />

            <Dialog open={resignOpen} onClose={() => setResignOpen(false)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>Raise my resignation</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mb: 1.5 }}>
                        Filed as {user?.loginId || '—'}. It goes to another admin for approval.
                    </Typography>
                    <ResignationForm
                        user={user} notify={notify}
                        onCancel={() => setResignOpen(false)}
                        onSubmitted={() => { setResignOpen(false); load(); }}
                    />
                </DialogContent>
            </Dialog>

            <SettlementDialog
                open={!!settlementFor}
                resignationId={settlementFor?.resignationId}
                employeeName={settlementFor?.employeeName}
                onClose={() => setSettlementFor(null)}
            />

            <ResignationSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} notify={notify} />
        </>
    );
}

const RES_COLS = 'minmax(210px, 1.5fr) 128px 128px 84px 34px';
const RES_MIN_W = 720;
const RES_PAD_X = 2;

const noticeOf = (r) => (r.noticeDays != null ? r.noticeDays : daysBetween(
    String(r.resignationDate).split('-').reverse().join('-'),
    String(r.lastWorkingDay).split('-').reverse().join('-'),
));

const daysRemainingOf = (r) => {
    if (r.daysRemaining != null) return r.daysRemaining;
    if (r.status !== 'Pending' && r.status !== 'Approved') return 0;
    return daysBetween(todayISO(), String(r.lastWorkingDay).split('-').reverse().join('-'));
};

const HeadCell = ({ children, align }) => (
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.6, textAlign: align || 'left' }}>
        {children}
    </Typography>
);

// One line per resignation so a screenful can be compared at a glance; the
// reason and the actions move into the detail drawer.
function ResignationRow({ r, busy, selected, onOpen }) {
    return (
        <Box
            onClick={() => onOpen(r)}
            sx={{
                display: 'grid', gridTemplateColumns: RES_COLS, alignItems: 'center', gap: 2,
                px: RES_PAD_X, py: 1.4, cursor: 'pointer',
                borderBottom: '1px solid #F1F0F9',
                borderLeft: `3px solid ${selected ? PRIMARY : 'transparent'}`,
                bgcolor: selected ? '#F8F6FF' : 'transparent',
                opacity: busy ? 0.55 : 1,
                transition: 'background-color .15s, opacity .15s',
                '&:hover': { bgcolor: selected ? '#F8F6FF' : '#FBFAFF' },
                '&:last-of-type': { borderBottom: 'none' },
            }}
        >
            <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Avatar sx={{ width: 36, height: 36, ...avatarTone(r.employeeName || r.employeeCode), fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {initials(r.employeeName || r.employeeCode)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#111827', minWidth: 0 }} noWrap>{r.employeeName || r.employeeCode}</Typography>
                        <StatusPill status={r.status} />
                    </Stack>
                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }} noWrap>
                        {r.employeeCode}{r.designation ? ` · ${r.designation}` : ''}{r.department ? ` · ${r.department}` : ''}
                    </Typography>
                </Box>
            </Stack>

            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151', fontVariantNumeric: 'tabular-nums' }} noWrap>
                {showDate(r.resignationDate)}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151', fontVariantNumeric: 'tabular-nums' }} noWrap>
                {showDate(r.lastWorkingDay)}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151', fontVariantNumeric: 'tabular-nums' }} noWrap>
                {noticeOf(r)} days
            </Typography>

            <Stack sx={{ alignItems: 'flex-end' }}>
                {busy
                    ? <CircularProgress size={16} />
                    : <ChevronRightRoundedIcon sx={{ fontSize: 20, color: '#C4CBD6' }} />}
            </Stack>
        </Box>
    );
}

// Everything that doesn't belong in a scannable row: the full reason, the
// timeline, and the decisions.
function ResignationDetail({ r, busy, readOnly, onClose, onApprove, onCancelNotice, onEdit, onRelieve, onWithdraw, onUnrelieve, onDelete, onViewSettlement }) {
    if (!r) return null;
    const notice = noticeOf(r);
    const remaining = daysRemainingOf(r);
    return (
        <Drawer
            anchor="right"
            open
            onClose={onClose}
            slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 }, borderTopLeftRadius: '14px', borderBottomLeftRadius: '14px' } } }}
        >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.4, px: 2.4, py: 2, borderBottom: '1px solid #F1F0F9' }}>
                <Avatar sx={{ width: 44, height: 44, ...avatarTone(r.employeeName || r.employeeCode), fontSize: 15, fontWeight: 700 }}>
                    {initials(r.employeeName || r.employeeCode)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }} noWrap>{r.employeeName || r.employeeCode}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#98A0AE' }} noWrap>
                        {r.employeeCode}{r.designation ? ` · ${r.designation}` : ''}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
            </Stack>

            <Box sx={{ px: 2.4, py: 2, flex: 1, overflowY: 'auto' }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
                    <StatusPill status={r.status} />
                    {r.department && <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{r.department}</Typography>}
                </Stack>

                <Box sx={{ bgcolor: '#FAFAFC', border: '1px solid #F1F0F9', borderRadius: '9px', px: 1.6, py: 0.4, mb: 2 }}>
                    {[
                        ['Resigned on', showDate(r.resignationDate)],
                        ...(r.noticePeriodStartDate ? [['Notice starts', showDate(r.noticePeriodStartDate)]] : []),
                        ['Last working day', showDate(r.lastWorkingDay)],
                        ['Notice period', `${notice} days`],
                        ...((r.status === 'Pending' || r.status === 'Approved') ? [['Days remaining', `${remaining} days`]] : []),
                    ].map(([l, v], i) => (
                        <Stack key={l} direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2, py: 1, borderTop: i === 0 ? 'none' : '1px solid #F1F0F9' }}>
                            <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{l}</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
                        </Stack>
                    ))}
                </Box>

                {(r.approvedBy || r.relievedBy || r.cancelledBy) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>Trail</Typography>
                        <Stack spacing={0.6}>
                            {r.approvedBy && (
                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                    Approved by <b>{r.approvedBy}</b>{r.approvedOn ? ` on ${showDate(r.approvedOn)}` : ''}
                                </Typography>
                            )}
                            {r.relievedBy && (
                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                    Relieved by <b>{r.relievedBy}</b>{r.relievedOn ? ` on ${showDate(r.relievedOn)}` : ''}
                                </Typography>
                            )}
                            {r.cancelledBy && (
                                <Typography sx={{ fontSize: 12.5, color: '#9A3412' }}>
                                    Notice cancelled by <b>{r.cancelledBy}</b>{r.cancelledOn ? ` on ${showDate(r.cancelledOn)}` : ''}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}

                {(r.reasonCategory || r.reason) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>Reason</Typography>
                        <Stack direction="row" spacing={1.2} sx={{ px: 1.4, py: 1.2, borderRadius: '9px', bgcolor: '#FAFAFD', border: '1px solid #F1F3F8', alignItems: 'flex-start' }}>
                            <FormatQuoteRoundedIcon sx={{ fontSize: 16, color: '#C4CBD6', flexShrink: 0, mt: '1px' }} />
                            <Box sx={{ minWidth: 0 }}>
                                {r.reasonCategory && (
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.4 }}>{r.reasonCategory}</Typography>
                                )}
                                {r.reason && <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mt: 0.2 }}>{r.reason}</Typography>}
                            </Box>
                        </Stack>
                    </Box>
                )}

                {r.remarks && (
                    <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>Remarks</Typography>
                        <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{r.remarks}</Typography>
                    </Box>
                )}
            </Box>

            {r.status === 'Relieved' && (
                <Box sx={{ px: 2.4, pt: 0, pb: readOnly ? 2 : 0 }}>
                    <Button
                        fullWidth startIcon={<ReceiptLongRoundedIcon sx={{ fontSize: 17 }} />}
                        onClick={onViewSettlement}
                        sx={{ ...tonalBtn, height: 38, fontSize: 12.5 }}
                    >
                        View Final Settlement
                    </Button>
                </Box>
            )}

            {!readOnly && r.status !== 'Withdrawn' && r.status !== 'Cancelled' && (
                <Stack direction="row" spacing={0.8} sx={{ px: 2.4, py: 2, borderTop: '1px solid #F1F0F9', flexWrap: 'wrap', gap: 0.8 }}>
                    {r.status === 'Pending' && (
                        <>
                            <Button disabled={busy} startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />} onClick={onApprove} sx={{ ...solidBtn, height: 36, fontSize: 12.5 }}>Approve</Button>
                            <Button disabled={busy} startIcon={<UndoRoundedIcon sx={{ fontSize: 16 }} />} onClick={onWithdraw} sx={{ ...ghostBtn, height: 36, fontSize: 12.5 }}>Decline</Button>
                        </>
                    )}
                    {r.status === 'Approved' && (
                        <>
                            <Button disabled={busy} startIcon={<HowToRegRoundedIcon sx={{ fontSize: 16 }} />} onClick={onRelieve} sx={{ ...solidBtn, height: 36, fontSize: 12.5, bgcolor: '#16A34A', '&:hover': { bgcolor: '#15803D' } }}>Relieve</Button>
                            <Button disabled={busy} startIcon={<EventBusyRoundedIcon sx={{ fontSize: 16 }} />} onClick={onCancelNotice} sx={{ ...ghostBtn, height: 36, fontSize: 12.5, color: '#9A3412', borderColor: '#FED7AA', '&:hover': { bgcolor: '#FFF7ED', borderColor: '#FDBA74' } }}>Cancel notice period</Button>
                        </>
                    )}
                    {r.status === 'Relieved' && (
                        <Button disabled={busy} startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />} onClick={onUnrelieve} sx={{ ...ghostBtn, height: 36, fontSize: 12.5 }}>Un-relieve</Button>
                    )}
                    {(r.status === 'Pending' || r.status === 'Approved') && (
                        <Button disabled={busy} startIcon={<EditRoundedIcon sx={{ fontSize: 16 }} />} onClick={onEdit} sx={{ ...ghostBtn, height: 36, fontSize: 12.5 }}>Edit record</Button>
                    )}
                    {r.status !== 'Relieved' && (
                        <Button disabled={busy} onClick={onDelete} sx={{ ...ghostBtn, height: 36, fontSize: 12.5, ml: 'auto', color: '#DC2626', borderColor: '#FECACA', '&:hover': { bgcolor: '#FEF2F2', borderColor: '#FCA5A5' } }}>Delete</Button>
                    )}
                </Stack>
            )}
        </Drawer>
    );
}

// The payslip-style Full & Final settlement statement — read-only, fetched by
// resignation id. Only rendered lines are shown; nothing here hard-codes a
// specific earning/deduction key, since the backend only sends non-zero ones.
// Opened by resignation id (Resignations screen) or by employee code (Employee detail page).
export function SettlementDialog({ open, resignationId, employeeCode, employeeName, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || (!resignationId && !employeeCode)) return;
        let cancelled = false;
        setLoading(true); setError(''); setData(null);
        (resignationId
            ? http.get(GetFinalSettlement, { params: { resignationId } })
            : http.get(GetEmployeeFinalSettlement, { params: { employeeCode } }))
            .then(({ data: body }) => {
                if (cancelled) return;
                if (body?.error) throw new Error(body.message || 'Could not load the settlement.');
                setData(body?.data || null);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.response?.status === 404
                    ? 'No Full & Final settlement found — it may have been reversed by an un-relieve.'
                    : apiErrorMessage(err, 'Could not load the settlement.'));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, resignationId, employeeCode]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <Stack direction="row" sx={{ alignItems: 'center', px: 2.5, py: 2, borderBottom: '1px solid #F1F0F9' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800 }}>Full & Final Settlement</Typography>
                    <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{employeeName}</Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
            </Stack>
            <DialogContent sx={{ p: 2.5 }}>
                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={24} /></Box>
                ) : error ? (
                    <EmptyState icon={ReceiptLongRoundedIcon} title="Settlement unavailable" hint={error} />
                ) : !data ? null : (
                    <Stack spacing={2}>
                        <Box sx={{ bgcolor: '#FAFAFC', border: '1px solid #F1F0F9', borderRadius: '9px', px: 1.6, py: 0.4 }}>
                            {[
                                ['Department', data.department],
                                ['Designation', data.designation],
                                ['Last working day', showDate(data.lastWorkingDay)],
                                ['Settlement month', data.settlementMonth],
                                ['Payable days', data.workingDaysInMonth != null ? `${data.payableDays} of ${data.workingDaysInMonth}` : data.payableDays],
                            ].filter(([, v]) => v != null && v !== '').map(([l, v], i) => (
                                <Stack key={l} direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2, py: 0.9, borderTop: i === 0 ? 'none' : '1px solid #F1F0F9' }}>
                                    <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{l}</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{v}</Typography>
                                </Stack>
                            ))}
                        </Box>

                        {Array.isArray(data.earnings) && data.earnings.length > 0 && (
                            <SettlementTable title="Earnings" lines={data.earnings} total={data.grossEarned} totalLabel="Gross earned" />
                        )}
                        {Array.isArray(data.deductions) && data.deductions.length > 0 && (
                            <SettlementTable title="Deductions" lines={data.deductions} total={data.totalDeductions} totalLabel="Total deductions" />
                        )}

                        <Box sx={{
                            borderRadius: '9px', px: 1.8, py: 1.4,
                            bgcolor: data.employeeOwes ? '#FEF2F2' : '#F0FDF4',
                            border: `1px solid ${data.employeeOwes ? '#FECACA' : '#BBF7D0'}`,
                        }}>
                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                                    {data.employeeOwes && <WarningAmberRoundedIcon sx={{ fontSize: 18, color: '#DC2626' }} />}
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: data.employeeOwes ? '#B91C1C' : '#166534' }}>
                                        {data.employeeOwes ? 'Recoverable from employee' : 'Net Settlement'}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ fontSize: 18, fontWeight: 800, color: data.employeeOwes ? '#DC2626' : '#15803D' }}>
                                    {inr(Math.abs(data.netSettlement || 0))}
                                </Typography>
                            </Stack>
                        </Box>

                        {(data.bankName || data.accountNumber) && (
                            <Box>
                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mb: 0.8 }}>
                                    <AccountBalanceRoundedIcon sx={{ fontSize: 15, color: '#98A0AE' }} />
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.6 }}>Bank details</Typography>
                                </Stack>
                                <Typography sx={{ fontSize: 12.5, color: '#475569', lineHeight: 1.7 }}>
                                    {data.bankName}{data.branch ? ` · ${data.branch}` : ''}<br />
                                    A/C {data.accountNumber}{data.ifsc ? ` · IFSC ${data.ifsc}` : ''}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2 }}>
                <Button onClick={onClose} sx={{ ...ghostBtn, height: 38 }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function SettlementTable({ title, lines, total, totalLabel }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>{title}</Typography>
            <Box sx={{ border: '1px solid #F1F0F9', borderRadius: '9px', overflow: 'hidden' }}>
                {lines.map((l, i) => (
                    <Stack key={l.key || l.label || i} direction="row" sx={{ justifyContent: 'space-between', px: 1.6, py: 1, borderTop: i === 0 ? 'none' : '1px solid #F1F0F9' }}>
                        <Typography sx={{ fontSize: 13, color: '#475569' }}>{l.label}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{inr(l.amount)}</Typography>
                    </Stack>
                ))}
                {total != null && (
                    <Stack direction="row" sx={{ justifyContent: 'space-between', px: 1.6, py: 1, bgcolor: '#FAFAFD', borderTop: '1px solid #F1F0F9' }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#6E6B99' }}>{totalLabel}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{inr(total)}</Typography>
                    </Stack>
                )}
            </Box>
        </Box>
    );
}

const SETTINGS_ROLES = [
    { id: USER_TYPE.EMPLOYEE, label: 'Employee' },
    { id: USER_TYPE.ADMIN, label: 'Admin' },
];

// Controls how long, after withdrawing a resignation, an employee must wait
// before submitting another. Small enough to live as a dialog off this page
// rather than its own settings screen.
function ResignationSettingsDialog({ open, onClose, notify }) {
    const [userTypeId, setUserTypeId] = useState(USER_TYPE.EMPLOYEE);
    const [days, setDays] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        http.get(GetResignationSetting, { params: { userTypeId } })
            .then(({ data: body }) => {
                if (body?.error) throw new Error(body.message);
                setDays(String(body?.data?.reapplyWaitingDays ?? 0));
            })
            .catch(() => setDays('0'))
            .finally(() => setLoading(false));
    }, [open, userTypeId]);

    const save = async () => {
        setSaving(true);
        try {
            const { data: body } = await http.put(SaveResignationSetting, {
                userTypeId, reapplyWaitingDays: Number(days) || 0,
            });
            if (body?.error) throw new Error(body.message || 'Could not save the setting.');
            notify('Resignation setting saved.');
            onClose();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the setting.'), 'error');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>Re-apply cooling period</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', mb: 1.6 }}>
                    How long, after withdrawing a resignation, that role must wait before submitting another.
                </Typography>
                <Stack spacing={1.4}>
                    <TextField
                        select label="Role" size="small" fullWidth
                        value={userTypeId} onChange={(e) => setUserTypeId(Number(e.target.value))}
                        sx={field}
                    >
                        {SETTINGS_ROLES.map((r) => <MenuItem key={r.id} value={r.id} sx={{ fontSize: 13.5 }}>{r.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        label="Waiting days" type="number" size="small" fullWidth
                        value={days} onChange={(e) => setDays(e.target.value)}
                        disabled={loading} sx={field}
                        slotProps={{ htmlInput: { min: 0 } }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                <Button onClick={onClose} disabled={saving} sx={{ ...ghostBtn, height: 40 }}>Cancel</Button>
                <Button onClick={save} disabled={saving || loading} sx={{ ...solidBtn, height: 40 }}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const addDaysISO = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// `formStyle` is the employee's own screen: a Material-style form with a live summary card beside it.
function ResignationForm({ user, notify, onSubmitted, onCancel, formStyle = false }) {
    const EMPTY = { lastWorkingDay: '', reasonCategory: '', reason: '', remarks: '' };
    const [form, setForm] = useState(EMPTY);
    const [tried, setTried] = useState(false);
    const [saving, setSaving] = useState(false);

    const today = todayISO();
    const notice = daysBetween(today, form.lastWorkingDay);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const valid = form.lastWorkingDay && form.reasonCategory && form.reason.trim()
        && form.lastWorkingDay >= today;

    const submit = async () => {
        setTried(true);
        if (!valid) {
            notify(form.lastWorkingDay && form.lastWorkingDay < today ? 'The last working day cannot be in the past.'
                : 'Fill in all the required fields.', 'warning');
            return;
        }
        setSaving(true);
        try {
            // employee code + resignation date come from the token/server — don't send them.
            const { data: body } = await http.post(SubmitResignation, {
                lastWorkingDay: toApiDate(form.lastWorkingDay),
                reasonCategory: form.reasonCategory,
                reason: form.reason.trim(),
                remarks: form.remarks.trim(),
            });
            if (body?.error) throw new Error(body.message || 'Could not submit your resignation.');
            notify('Resignation submitted — pending approval.');
            onSubmitted?.();
        } catch (err) {
            const status = err?.response?.status;
            notify(status === 403
                ? 'Self-service resignation isn’t enabled for your account yet. Please ask your admin to raise it.'
                : apiErrorMessage(err, 'Could not submit your resignation.'), 'error');
        } finally { setSaving(false); }
    };

    if (formStyle) {
        const outlined = {
            '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#FBFAFF', fontSize: 14.5, '& fieldset': { borderColor: '#D9D4EE' }, '&:hover fieldset': { borderColor: '#B7ABF0' }, '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 2 } },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
        };
        const chip = (on) => ({
            height: 34, fontSize: 13, fontWeight: 600, bgcolor: on ? '#E9E1FF' : '#fff', color: on ? '#4A2FB8' : '#49454F',
            border: `1px solid ${on ? '#C8B9FA' : '#D9D4EE'}`, '&:hover': { bgcolor: on ? '#E9E1FF' : '#F3EEFF' },
        });
        const dateBad = tried && (!form.lastWorkingDay || form.lastWorkingDay < today);
        const catBad = tried && !form.reasonCategory;
        const reasonBad = tried && !form.reason.trim();

        return (
            <Box sx={{ bgcolor: '#fff', border: '1px solid #E8E4F5', borderRadius: '20px', p: { xs: 2.2, md: 3.2 } }}>
                <Stack spacing={3}>
                    {/* Row 1: when */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 2.4, alignItems: 'start' }}>
                        <Box>
                            <TextField
                                label="Last working day" type="date" fullWidth required
                                value={form.lastWorkingDay} onChange={set('lastWorkingDay')}
                                error={dateBad} helperText={dateBad ? 'Pick today or a later date' : ' '}
                                sx={outlined} slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }}
                            />
                            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.9, mt: -0.4 }}>
                                {[30, 60, 90].map((d) => {
                                    const iso = addDaysISO(d);
                                    return <Chip key={d} label={`In ${d} days`} onClick={() => setForm((f) => ({ ...f, lastWorkingDay: iso }))} sx={chip(form.lastWorkingDay === iso)} />;
                                })}
                            </Stack>
                        </Box>
                        <Box sx={{
                            minHeight: 56, px: 2.2, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                            bgcolor: form.lastWorkingDay ? '#F3EDFF' : '#F6F5FA', border: `1px solid ${form.lastWorkingDay ? '#E3D9FB' : '#ECEAF3'}`,
                        }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: form.lastWorkingDay ? '#6B5BB5' : '#8E8A9F' }}>
                                {form.lastWorkingDay ? `Last day · ${showDate(form.lastWorkingDay)}` : 'Pick a date to see your notice period'}
                            </Typography>
                            {form.lastWorkingDay && (
                                <Typography sx={{ fontSize: 22, fontWeight: 600, color: '#4A2FB8', whiteSpace: 'nowrap' }}>
                                    {notice} <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600 }}>day{notice === 1 ? '' : 's'}</Box>
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Row 2: why */}
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: catBad ? '#B3261E' : '#49454F', mb: 1.2 }}>
                            Reason category <Box component="span" sx={{ color: '#B3261E' }}>*</Box>{catBad ? ' — pick one' : ''}
                        </Typography>
                        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {REASON_CATEGORIES.map((c) => {
                                const on = form.reasonCategory === c;
                                return (
                                    <Chip key={c} label={c} onClick={() => setForm((f) => ({ ...f, reasonCategory: c }))}
                                        icon={on ? <CheckRoundedIcon sx={{ fontSize: '17px !important', color: '#4A2FB8 !important' }} /> : undefined}
                                        sx={chip(on)} />
                                );
                            })}
                        </Stack>
                    </Box>

                    {/* Row 3: message */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 2fr)' }, gap: 2.4 }}>
                        <TextField
                            label="Reason" required fullWidth multiline minRows={4} value={form.reason} onChange={set('reason')}
                            error={reasonBad} helperText={reasonBad ? 'Tell your admin why you’re resigning' : ' '}
                            sx={outlined} slotProps={{ inputLabel: { shrink: true } }}
                            placeholder="Tell your admin why you’re resigning."
                        />
                        <TextField
                            label="Remarks (optional)" fullWidth multiline minRows={4} value={form.remarks} onChange={set('remarks')}
                            helperText=" " sx={outlined} slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Box>

                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.2 }}>
                        <Typography sx={{ fontSize: 12, color: '#6B6B76' }}>Filed as {user?.loginId || user?.name || 'you'} · goes to your admin for approval · you can withdraw while pending</Typography>
                        <Stack direction="row" spacing={1.2}>
                            <Button onClick={() => { setForm(EMPTY); setTried(false); }} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: PRIMARY, borderRadius: '20px', px: 2.5, height: 44 }}>Clear</Button>
                            <Button
                                onClick={submit} disabled={saving}
                                startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <LogoutRoundedIcon sx={{ fontSize: 18 }} />}
                                sx={{ ...solidBtn, height: 44, px: 3.4, borderRadius: '22px' }}
                            >
                                {saving ? 'Submitting…' : 'Submit resignation'}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
        );
    }

    return (
        <Stack spacing={2.2}>
            {/* slotProps, not the removed InputLabelProps: without a shrunk label
                an empty date input renders its own dd-mm-yyyy placeholder
                underneath the label text. Every field shrinks its label so they line up. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                <TextField
                    label="Last working day" type="date" size="small" fullWidth required
                    value={form.lastWorkingDay} onChange={set('lastWorkingDay')}
                    error={tried && (!form.lastWorkingDay || form.lastWorkingDay < today)}
                    sx={field}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }}
                />
                {/* The notice period updates as soon as a last working day is picked. */}
                <Box sx={{
                    px: 1.8, borderRadius: '8px', minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                    bgcolor: form.lastWorkingDay ? '#EEF2FF' : '#F5F7FA', border: `1px solid ${form.lastWorkingDay ? '#C7D2FE' : '#E6EAF1'}`,
                }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: form.lastWorkingDay ? '#4338CA' : '#98A0AE' }}>
                        {form.lastWorkingDay ? 'Notice period' : 'Pick a date to see the notice period'}
                    </Typography>
                    {form.lastWorkingDay && (
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#4338CA' }}>{notice} day{notice === 1 ? '' : 's'}</Typography>
                    )}
                </Box>
            </Box>

            <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tried && !form.reasonCategory ? '#E11D48' : '#374151', mb: 1 }}>
                    Why are you leaving? <Box component="span" sx={{ color: '#E11D48' }}>*</Box>
                </Typography>
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.9 }}>
                    {REASON_CATEGORIES.map((c) => {
                        const on = form.reasonCategory === c;
                        return (
                            <Chip
                                key={c} label={c} onClick={() => setForm((f) => ({ ...f, reasonCategory: c }))}
                                sx={{
                                    height: 32, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                                    bgcolor: on ? PRIMARY : '#fff', color: on ? '#fff' : '#475569',
                                    border: `1px solid ${on ? PRIMARY : '#E2E8F0'}`,
                                    '&:hover': { bgcolor: on ? PRIMARY : '#F1EEFE' },
                                }}
                            />
                        );
                    })}
                </Stack>
            </Box>

            <TextField
                label="Reason" size="small" fullWidth multiline minRows={3} required
                value={form.reason} onChange={set('reason')}
                error={tried && !form.reason.trim()} sx={field}
                placeholder="Tell your admin why you’re resigning."
                slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
                label="Remarks (optional)" size="small" fullWidth multiline minRows={2}
                value={form.remarks} onChange={set('remarks')}
                sx={field}
                slotProps={{ inputLabel: { shrink: true } }}
            />

            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                {onCancel && <Button onClick={onCancel} disabled={saving} sx={{ ...ghostBtn, height: 42, px: 2.5 }}>Cancel</Button>}
                <Button
                    onClick={submit} disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <LogoutRoundedIcon sx={{ fontSize: 18 }} />}
                    sx={{ ...solidBtn, height: 42, px: 3 }}
                >
                    {saving ? 'Submitting…' : 'Submit resignation'}
                </Button>
            </Stack>
        </Stack>
    );
}

// The employee's own "My Resignation" screen: fetches their current
// resignation (if any) and shows a status view with a Withdraw action, or
// falls back to the submit form when there's nothing active to show.
function SelfRequestView({ user, notify }) {
    const [loading, setLoading] = useState(true);
    const [mine, setMine] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [withdraw, setWithdraw] = useState(null); // { remarks }
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetMyResignation);
            if (body?.error) throw new Error(body.message || 'Could not load your resignation.');
            const d = body?.data;
            const list = Array.isArray(d) ? d : (Array.isArray(d?.resignations) ? d.resignations : (d ? [d] : []));
            const latest = list.map(mapResignationRow)[0] || null;
            setMine(latest && latest.status !== 'Withdrawn' && latest.status !== 'Cancelled' ? latest : null);
            setLoadError('');
        } catch (err) {
            if (err?.response?.status === 404) { setMine(null); setLoadError(''); }
            else setLoadError(apiErrorMessage(err, 'Could not load your resignation.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doWithdraw = async () => {
        setBusy(true);
        try {
            const { data: body } = await http.put(WithdrawResignation, { remarks: withdraw.remarks || '' });
            if (body?.error) throw new Error(body.message || 'Could not withdraw your resignation.');
            notify('Resignation withdrawn.');
            setWithdraw(null);
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not withdraw your resignation.'), 'error');
        } finally { setBusy(false); }
    };

    if (loading) {
        return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress size={26} /></Box>;
    }

    if (loadError) {
        return (
            <Box sx={{ width: '100%' }}>
                <PageHeader title="My Resignation" subtitle="Your own resignation request and its status." />
                <Box sx={{ ...card, p: 0 }}><EmptyState icon={LogoutRoundedIcon} title="Couldn't load your resignation" hint={loadError} /></Box>
            </Box>
        );
    }

    if (!mine) {
        return (
            <Box sx={{ width: '100%', pt: 1, pb: 3 }}>
                <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#0F172A', lineHeight: 1.25 }}>Resignation</Typography>
                <Typography sx={{ fontSize: 13.5, color: '#6B7280', mt: 0.4, mb: 2 }}>Raise your resignation. It goes to your admin for approval.</Typography>
                <ResignationForm user={user} notify={notify} onSubmitted={load} formStyle />
            </Box>
        );
    }

    const notice = noticeOf(mine);
    const remaining = daysRemainingOf(mine);
    const canWithdraw = mine.status === 'Pending'; // past Pending the server answers 409 — contact HR
    const serving = mine.status === 'Pending' || mine.status === 'Approved';

    return (
        <Box sx={{ width: '100%' }}>
            <PageHeader title="My Resignation" subtitle="Your own resignation request and its status." />
            <Box sx={{ ...card, p: { xs: 1.8, md: 2.4 } }}>
                <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: STATUS_STYLE[mine.status]?.bg || '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {mine.status === 'Relieved'
                            ? <DoneAllRoundedIcon sx={{ fontSize: 26, color: STATUS_STYLE[mine.status]?.color }} />
                            : <HourglassEmptyRoundedIcon sx={{ fontSize: 26, color: STATUS_STYLE[mine.status]?.color }} />}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }}>Your resignation</Typography>
                            <StatusPill status={mine.status} />
                        </Stack>
                        <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.3 }}>
                            {mine.status === 'Pending' && 'Awaiting your admin’s decision.'}
                            {mine.status === 'Approved' && 'Approved — you’re serving your notice period.'}
                            {mine.status === 'Relieved' && 'You have been relieved.'}
                        </Typography>
                    </Box>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ bgcolor: '#FAFAFC', border: '1px solid #F1F0F9', borderRadius: '9px', px: 1.6, py: 0.4, mb: 2 }}>
                    {[
                        ['Resigned on', showDate(mine.resignationDate)],
                        ['Last working day', showDate(mine.lastWorkingDay)],
                        ['Notice period', `${notice} days`],
                        ...(mine.noticePeriodStartDate ? [['Notice starts', showDate(mine.noticePeriodStartDate)]] : []),
                        ...(serving ? [['Days remaining', `${remaining} days`]] : []),
                    ].map(([l, v], i) => (
                        <Stack key={l} direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2, py: 1, borderTop: i === 0 ? 'none' : '1px solid #F1F0F9' }}>
                            <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{l}</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
                        </Stack>
                    ))}
                </Box>

                {(mine.approvedBy || mine.relievedBy) && (
                    <Stack spacing={0.6} sx={{ mb: 2 }}>
                        {mine.approvedBy && (
                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                Approved by <b>{mine.approvedBy}</b>{mine.approvedOn ? ` on ${showDate(mine.approvedOn)}` : ''}
                            </Typography>
                        )}
                        {mine.relievedBy && (
                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                Relieved by <b>{mine.relievedBy}</b>{mine.relievedOn ? ` on ${showDate(mine.relievedOn)}` : ''}
                            </Typography>
                        )}
                    </Stack>
                )}

                {(mine.reasonCategory || mine.reason) && (
                    <Box sx={{ mb: canWithdraw ? 2 : 0 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>Your reason</Typography>
                        <Stack direction="row" spacing={1.2} sx={{ px: 1.4, py: 1.2, borderRadius: '9px', bgcolor: '#FAFAFD', border: '1px solid #F1F3F8', alignItems: 'flex-start' }}>
                            <FormatQuoteRoundedIcon sx={{ fontSize: 16, color: '#C4CBD6', flexShrink: 0, mt: '1px' }} />
                            <Box sx={{ minWidth: 0 }}>
                                {mine.reasonCategory && (
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.4 }}>{mine.reasonCategory}</Typography>
                                )}
                                {mine.reason && <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mt: 0.2 }}>{mine.reason}</Typography>}
                            </Box>
                        </Stack>
                    </Box>
                )}

                {canWithdraw && (
                    <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                        <Button
                            startIcon={<UndoRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setWithdraw({ remarks: '' })}
                            sx={{ ...ghostBtn, height: 38, fontSize: 12.5 }}
                        >
                            Withdraw
                        </Button>
                    </Stack>
                )}
            </Box>

            <Dialog open={!!withdraw} onClose={() => setWithdraw(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>Withdraw your resignation?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 1.5 }}>
                        This cancels your resignation and you stay active. A waiting period may apply before you can
                        submit another.
                    </Typography>
                    <TextField
                        label="Remarks (optional)" size="small" fullWidth multiline minRows={2}
                        value={withdraw?.remarks || ''} onChange={(e) => setWithdraw((w) => ({ ...w, remarks: e.target.value }))}
                        sx={field}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                    <Button onClick={() => setWithdraw(null)} disabled={busy} sx={{ ...ghostBtn, height: 40 }}>Cancel</Button>
                    <Button onClick={doWithdraw} disabled={busy} sx={{ ...solidBtn, height: 40 }}>
                        {busy ? 'Withdrawing…' : 'Withdraw'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
